import extend from 'lodash/extend';
import orderBy from 'lodash/orderBy';
import get from 'lodash/get';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import {
  CustomComponent,
  CUSTOM_NOTE_CONTEXT,
  CustomNoteLabel,
  getProductExtendedStructureType, ngHelper,
  IProductTypeOverview, IProductExtendedStructureType,
  PRODUCT_TYPE_STRUCTURE_TYPES,
  getImageShape, IMAGE_SHAPE,
  getEffectiveMaxQuantity,
} from '@cakeiteasy/web-utils';
import { CART_LINE_TYPES, ICakeCartLine } from '../../../cart';
import { CUSTOM_NOTE_COLOR_MAP } from '@cakeiteasy/web-ui';
import { getCustomNoteLabel } from '../../../helpers';

@Component({
  standalone: false,
  selector: 'cake-item',
  encapsulation: ViewEncapsulation.None,
  host: { class: 'ws-product-card-host' },
  templateUrl: './cake-item.component.html',
})
export class CakeItemComponent extends CustomComponent implements OnInit, OnChanges {
  @Input() cartLines: ICakeCartLine[];
  @Input() cake: IProductTypeOverview;
  @Input() showBakeryLine: boolean;
  @Input() buildCakeUrl: string;
  @Input() isTouchDevice: boolean;
  @Input() bakeryOverviewUrl: string;
  @Input() defaultCurrency: string;
  @Input() showCustomNote = false;
  @Input() noteContext: CUSTOM_NOTE_CONTEXT = CUSTOM_NOTE_CONTEXT.WEBSHOP;

  @Output() onShowDelivery = new EventEmitter<void>();
  @Output() onShowPickup = new EventEmitter<void>();
  @Output() onPiecesCakeOrder = new EventEmitter<{ cartLine: ICakeCartLine, productType?: IProductTypeOverview }>();
  @Output() onRemoveOrderLine = new EventEmitter<ICakeCartLine>();
  @Output() onRemoveCateringMenu = new EventEmitter<number>();
  @Output() onDetailsRequest = new EventEmitter<{
    productTypeId: number, productTypeName?: string, orderLineBuilderId?: string,
    /** @Deprecated */
    cartLineId?: number
  }>();
  @ViewChild('pickUpDropDown', { static: false }) pickUpDropDown: any;
  @ViewChild('deliveryDropDown', { static: false }) deliveryDropDown: any;

  public cartCount = 0;
  public cateringBuilds: ICakeCartLine[][] = [];
  public hasCateringBuilds = false;
  public latestCateringBuildLine: ICakeCartLine;
  public bakeryCurrency: string;
  public count$ = new Subject<number>();
  public piecesCakeWithOneVariantCartLine: ICakeCartLine;
  public isPiecesWithMultipleVariants: boolean;
  public isControlsShowing: boolean;
  public extendedStructureType = {} as IProductExtendedStructureType;
  public CUSTOM_NOTE_COLOR_MAP = CUSTOM_NOTE_COLOR_MAP;
  public showSameDayBadge: boolean;
  public imageHeightClass: string;
  /** modifiers of .ws-product-card, shared by every card variant */
  public cardClasses: { [klass: string]: boolean } = {};

  /** which card variant renders this product (a pieces product without a quantity picker turns into a pieces group
   * card once it is in the cart) */
  public get cardType(): 'request' | 'complex' | 'pieces' | 'catering' | 'pieces-group' | 'plain' {
    if (this.isRequestForm) {
      return 'request';
    }

    if (this.extendedStructureType.complex) {
      return 'complex';
    }

    if (this.showPiecesOverlay) {
      return 'pieces';
    }

    if (this.isCateringMenu) {
      return 'catering';
    }

    return this.showPiecesGroupOverlay ? 'pieces-group' : 'plain';
  }

  public get noteLabel(): CustomNoteLabel | null {
    return this.cake ? getCustomNoteLabel(this.cake, this.noteContext) : null;
  }

  public get isRequestForm() {
    if (!this.cake) {
      return false;
    }

    return this.cake.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.REQUEST_FORM;
  }

  public get isCateringMenu() {
    if (!this.cake) {
      return false;
    }

    return this.cake.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.CATERING_MENU;
  }

  public get isPiecesWithOneVariant() {
    if (!this.cake) {
      return false;
    }

    const products = this.cake.productIds || [];
    return this.cake.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.PIECES && products.length === 1;
  }

  public get cartLine() {
    const { PRODUCT } = CART_LINE_TYPES;

    const relatedLines = (this.cartLines || [])
      .filter(line => line.type === PRODUCT && (this.isCateringMenu
        ? line.parentType === this.cake.id && !!line.orderLineBuilderId
        : line.productType?.id === this.cake.id));
    const line = orderBy(relatedLines, 'id', 'desc')[0];
    return line || null;
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'cartLines', (lines) => {
      if (this.isCateringMenu) {
        this._updateCateringBuilds(lines);
      } else {
        this.cartCount = this._getCountFromCartLines(this.cake.id, lines);
      }

      if (this.isPiecesWithOneVariant) {
        const { PRODUCT } = CART_LINE_TYPES;
        this.piecesCakeWithOneVariantCartLine = lines
          .find(({ type, productType }) => type === PRODUCT && productType && productType.id === this.cake.id);
      }
    });
  }

  private _updateCateringBuilds(lines: ICakeCartLine[]) {
    const groups = new Map<string, ICakeCartLine[]>();
    (lines || []).filter(line => line.parentType === this.cake.id && line.orderLineBuilderId)
      .forEach(line => {
        const group = groups.get(line.orderLineBuilderId) || [];
        group.push(line);
        groups.set(line.orderLineBuilderId, group);
      });

    this.cateringBuilds = Array.from(groups.values());
    this.hasCateringBuilds = this.cateringBuilds.length > 0;

    const buildLines = this.cateringBuilds.map(([line]) => line);
    this.latestCateringBuildLine = orderBy(buildLines, 'id', 'desc')[0] || buildLines[buildLines.length - 1];
    this.cartCount = 0;
  }

  ngOnInit() {
    this.bakeryCurrency = this.defaultCurrency || get(this.cake, 'bakery.country.currencySymbol');
    this.extendedStructureType = getProductExtendedStructureType(this.cake.structureType);

    this.isPiecesWithMultipleVariants = this.extendedStructureType.pieces && this.cake.productIds.length > 1;

    const sameDayDelivery = this.cake && this.cake.sameDayDeliverySettings;
    this.showSameDayBadge = !!(
      sameDayDelivery && sameDayDelivery.isAvailableForSameDayDelivery &&
      sameDayDelivery.showBadge && sameDayDelivery.badgeText
    );

    this.imageHeightClass = this.getImageHeightClass();
    this.cardClasses = {
      'ws-product-card--no-image': !this.cake.image,
      'ws-product-card--with-delivery-controls': !!(this.showBakeryLine && this.cake.image),
      ...(this.imageHeightClass && { [this.imageHeightClass]: true }),
    };

    this._subscribe();
  }

  private getImageHeightClass(): string | null {
    const image = this.cake && this.cake.image;
    return this.getImageShapeCssClass(getImageShape(image));
  }

  private getImageShapeCssClass(shape: IMAGE_SHAPE): string | null {
    switch (shape) {
      case IMAGE_SHAPE.VERTICAL:
        return 'ws-product-card--image-tall';
      case IMAGE_SHAPE.SQUARE:
        return 'ws-product-card--image-square';
      case IMAGE_SHAPE.HORIZONTAL:
      default:
        return null;
    }
  }

  public orderNow() {
    this.onDetailsRequest.emit({ productTypeId: this.cake.id, productTypeName: this.cake.name });
  }

  public orderNowPiecesCake() {
    if (this.piecesCakeWithOneVariantCartLine) {
      this.onDetailsRequest.emit({
        productTypeId: this.cake.id,
        productTypeName: this.cake.name,
        orderLineBuilderId: this.piecesCakeWithOneVariantCartLine.orderLineBuilderId,
        /** @Deprecated */
        cartLineId: this.piecesCakeWithOneVariantCartLine.id
      });
    } else {
      this.orderNow();
    }
  }

  public saveOnePiecesCake(cartLine?: ICakeCartLine, count?: number) {
    const max = getEffectiveMaxQuantity(this.cake);
    const min = this.cake.minQuantity;
    const targetCount = count !== undefined ? count : min;

    const nextCount = max !== null ? Math.min(max, targetCount) : targetCount;

    if (!cartLine) {
      cartLine = {
        productId: this.cake.productIds[0],
        count: nextCount,
      } as ICakeCartLine;

      // TODO: it is necessary to find out should we use bakeryId, because we don't have bakery for express ordering cake list
      if (this.cake.bakery && this.cake.bakery.id) {
        extend(cartLine, { bakeryId: this.cake.bakery.id });
      }
    } else {
      cartLine = {
        ...cartLine,
        count: nextCount
      };
    }

    this.cartCount = cartLine.count;
    this.onPiecesCakeOrder.emit({ cartLine, productType: this.cake });
  }

  public editExisting() {
    this._editLine(this.cartLine);
  }

  public editCateringExisting() {
    this._editLine(this.latestCateringBuildLine);
  }

  private _editLine(line: ICakeCartLine) {

    this.onDetailsRequest.emit({
      productTypeId: this.cake.id,
      productTypeName: this.cake.name,
      orderLineBuilderId: line?.orderLineBuilderId,
      /** @Deprecated */
      cartLineId: line?.id
    });
  }

  public onCakeNameClick() {
    if (this.isPiecesWithOneVariant) {
      return this.orderNowPiecesCake();
    }

    if (this.hasCartLines) {
      this.editExisting();
    } else {
      this.orderNow();
    }
  }

  public showControls() {
    this.isControlsShowing = true;
    setTimeout(() => this.isControlsShowing = false, 3500);
  }

  public hideControls() {
    setTimeout(() => this.isControlsShowing = false, 10);
  }

  public get showPiecesQuantityPicker() {
    return (this.cake.showQuantityPicker || !this.cartCount);
  }

  private get isPiecesProduct() {
    return this.extendedStructureType.pieces && this.isPiecesWithOneVariant;
  }

  public get showPiecesOverlay() {
    return this.isPiecesProduct && this.showPiecesQuantityPicker;
  }

  public get showPiecesGroupOverlay() {
    return !this.isCateringMenu && (this.extendedStructureType.piecesGroup || this.isPiecesWithMultipleVariants ||
      (this.isPiecesProduct && !this.showPiecesQuantityPicker));
  }

  public get hasCartLines() {
    if (this.isCateringMenu) {
      return this.hasCateringBuilds;
    }

    return !!this.cartCount;
  }

  public onComplexOverlayClick() {
    if (this.hasCartLines) {
      this.showControls();
    } else {
      this.orderNow();
    }
  }

  public onPiecesOverlayClick() {
    if (this.isTouchDevice && !this.cartCount) {
      this.orderNowPiecesCake();
    }
  }

  public onPiecesGroupOverlayClick() {
    if (this.hasCartLines) {
      this.showControls();
    } else {
      this.orderNow();
    }
  }

  public onCateringOverlayClick() {
    if (this.hasCateringBuilds) {
      this.showControls();
    } else {
      this.orderNow();
    }
  }

  private _getCountFromCartLines(cakeId: number, lines: ICakeCartLine[]) {
    const { PRODUCT } = CART_LINE_TYPES;
    return (lines || [])
      .filter(line => line.type === PRODUCT && (this.isCateringMenu
        ? line.parentType === cakeId && !!line.orderLineBuilderId
        : line.productType?.id === cakeId))
      .reduce((result, line) => {
        result += line.count;
        return result;
      }, 0);
  }

  private _subscribe() {
    this.addSubscriber(this.count$.subscribe((count) => {
      if (this.piecesCakeWithOneVariantCartLine) {
        if (count === 0) {
          this.onRemoveOrderLine.emit(this.piecesCakeWithOneVariantCartLine);
        } else {
          this.piecesCakeWithOneVariantCartLine = {
            ...this.piecesCakeWithOneVariantCartLine,
            count
          };
          this.saveOnePiecesCake(this.piecesCakeWithOneVariantCartLine, count);
        }
      } else {
        this.saveOnePiecesCake(null, count);
      }
    }));
  }
}
